"use client";

import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Instagram, Twitter, Facebook, Youtube, Loader, ArrowRight } from "lucide-react";
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-4">
        {[1, 2, 3, 4].map(i => <li key={i} className="h-4 w-24 bg-muted animate-pulse rounded-lg" />)}
      </ul>;
  }
  return <ul className="space-y-4">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="text-foreground/70 hover:text-accent transition-colors font-default text-sm tracking-wide">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-4">
        <p className="text-accent font-medium text-sm">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <div className="flex flex-col sm:flex-row gap-4 items-end">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 w-full">
              <FormControl>
                <Input type="email" placeholder="Your email address" className="bg-transparent border-0 border-b border-foreground/20 rounded-lg h-12 px-0 focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-foreground/30" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase tracking-tighter" />
            </FormItem>} />
        <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-accent text-accent-foreground font-bold uppercase tracking-widest rounded-lg hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all shrink-0">
          {isSubmitting ? <Loader className="animate-spin size-4" /> : <span className="flex items-center gap-2">
              Join <ArrowRight className="size-4" />
            </span>}
        </Button>
      </div>
      {submitError && <p className="text-destructive text-[10px] uppercase mt-2">{submitError}</p>}
    </Form>;
}
export default function Footer() {
  const addressLines = ["700 Linden St, Lincoln"];
  const supportLinks = [{
    label: "Shipping & Returns",
    href: "/ShippingReturns"
  }, {
    label: "Privacy Policy",
    href: "/PrivacyPolicy"
  }, {
    label: "Terms of Service",
    href: "/TermsOfService"
  }, {
    label: "Contact Us",
    href: "/CustomerCarePage"
  }];
  const socialIcons = [{
    icon: Instagram,
    label: "Instagram",
    href: "#"
  }, {
    icon: Twitter,
    label: "Twitter",
    href: "#"
  }, {
    icon: Facebook,
    label: "Facebook",
    href: "#"
  }, {
    icon: Youtube,
    label: "Youtube",
    href: "#"
  }];
  const paymentMethods = [{
    name: "Visa",
    icon: ""
  }, {
    name: "Mastercard",
    icon: ""
  }, {
    name: "Apple Pay",
    icon: ""
  }, {
    name: "PayPal",
    icon: "🅿"
  }];
  return <footer data-section-id="4474" id="site-footer" className="bg-background text-foreground pt-24 pb-12 px-6 md:px-12 lg:px-16 border-t border-border">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-20">
          <div className="lg:col-span-5 space-y-8">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-7 w-auto" />
            </Link>
            <h2 className="heading text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight max-w-md">
              Elevating the <span className="text-accent italic">everyday</span> through curated design.
            </h2>
            <div className="flex gap-5">
              {socialIcons.map((social, i) => <Link key={social.label} data-index={i} to={social.href} aria-label={social.label} className="text-foreground/60 hover:text-accent transition-all transform hover:-translate-y-1">
                  <social.icon className="size-5 stroke-[1.25px]" />
                </Link>)}
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
            <div className="bg-secondary/50 p-8 md:p-10 rounded-lg border border-border/50">
              <p className="heading text-sm uppercase tracking-[0.2em] text-accent mb-4">
                The Golden Circle
              </p>
              <p className="body text-foreground/70 mb-8 text-lg">
                Subscribe to receive early access to new collections and exclusive atelier updates.
              </p>
              <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
              email: ""
            }} successMessage="Welcome to the atelier.">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>

        <Separator className="bg-border/60 mb-16" />

        {}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-12 mb-20">
          <div className="col-span-2 lg:col-span-2">
            <p className="heading text-xs uppercase tracking-widest text-foreground/40 mb-8">
              Atelier Location
            </p>
            <address className="not-italic body text-base text-foreground/70 space-y-2">
              {addressLines.map((line, i) => <div data-index={i} key={i}>{line}</div>)}
              <div className="pt-4">
                <Link to="mailto:info@lushfashionstore.com" className="text-accent hover:underline">
                  info@lushfashionstore.com
                </Link>
              </div>
            </address>
          </div>

          <div>
            <p className="heading text-xs uppercase tracking-widest text-foreground/40 mb-8">
              Collections
            </p>
            <MenuProvider menu_id="23">
              <FooterNavConsumer />
            </MenuProvider>
          </div>

          <div>
            <p className="heading text-xs uppercase tracking-widest text-foreground/40 mb-8">
              Customer Care
            </p>
            <ul className="space-y-4">
              {supportLinks.map((link, i) => <li data-index={i} key={link.label}>
                  <Link to={link.href} className="text-foreground/70 hover:text-accent transition-colors text-sm tracking-wide">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="hidden lg:block">
            <p className="heading text-xs uppercase tracking-widest text-foreground/40 mb-8">
              Payment
            </p>
            <div className="flex flex-wrap gap-3">
              {paymentMethods.map((method, i) => <div key={method.name} data-index={i} className="w-10 h-7 bg-secondary border border-border rounded-lg flex items-center justify-center text-[10px] grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all" title={method.name}>
                  {method.name.charAt(0)}
                </div>)}
            </div>
          </div>
        </div>

        <Separator className="bg-border/60 mb-8" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="body text-[11px] text-foreground/40 uppercase tracking-widest">
            &copy; {new Date().getFullYear()} Lush Fashion Atelier. All rights reserved.
          </p>
          <div className="flex items-center gap-8">
            <Link to="#" className="body text-[11px] text-foreground/40 hover:text-accent uppercase tracking-widest transition-colors">
              Privacy
            </Link>
            <Link to="#" className="body text-[11px] text-foreground/40 hover:text-accent uppercase tracking-widest transition-colors">
              Terms
            </Link>
            <div className="flex items-center gap-2 text-[11px] text-foreground/20 uppercase tracking-widest">
              <span>Designed with</span>
              <span className="text-accent">Gold</span>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}